import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { useUniverseSecurityItems } from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';
import { IndexPickerRow } from '../Universe/IndexPickerRow';
import { computeUniverseStats } from '../Universe/computeUniverseStats';
import { FilterCombobox } from '../FilterCombobox';
import {
  formatScopeLabel,
  marketOf,
  MARKET_ORDER,
} from '../../config/constants/pointInTimeData';
import { STATIC_COUNTRY_COUNTS } from '../../config/constants/staticUniverses';
import type { Universe } from '@biasafe.models';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { universeSecuritiesQuery } from '@/shared/config/queryDefaults';
import { useLatestReconstitutionDate } from '../../../RebalancingSchedule/hooks/useLatestReconstitutionDate';

interface BenchmarkSearchPopoverProps {
  /** Universes NOT already selected as benchmarks (multi-select: a chosen one
   *  leaves this list). */
  availableBenchmarks: Array<Universe>;
  onSelect: (id: string) => void;
}

// Benchmarks ARE Morningstar indices, so this dropdown reuses the SAME row as
// the universe index picker — IndexPickerRow — with the identical
// coverage • N Countries • N Constituents meta, provider label + logo, and NO
// code badge. The only difference is selection: benchmarks are multi-select, so
// a chosen index just leaves the list (its code shows on the selected chips
// below, never here).
const officialNameFor = (u: Universe): string => u.officialName || u.name;
const benchmarkCodeFor = (u: Universe): string => u.benchmarkCode || u.id;

export function BenchmarkSearchPopover({
  availableBenchmarks,
  onSelect,
}: BenchmarkSearchPopoverProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState('All');
  const [marketFilter, setMarketFilter] = useState('All');

  // Same security-items query as the universe picker (same params → shared
  // React Query cache) so both dropdowns show identical country/constituent
  // counts from one pass.
  const asOf = useLatestReconstitutionDate();
  const { data: allSecurities } = useUniverseSecurityItems(
    asOf ? { as_of: asOf } : undefined,
    { query: { ...universeSecuritiesQuery(asOf) } },
  );
  const universeStats = useMemo(
    () => computeUniverseStats(allSecurities),
    [allSecurities],
  );

  const providerOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of availableBenchmarks) {
      if (u.dataProvider) set.add(u.dataProvider);
    }
    return [...set];
  }, [availableBenchmarks]);

  const marketOptions = useMemo(() => {
    const set = new Set(
      availableBenchmarks.map((u) => marketOf(u.coverageLabel)),
    );
    return MARKET_ORDER.filter((m) => set.has(m));
  }, [availableBenchmarks]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return availableBenchmarks.filter((u) => {
      if (providerFilter !== 'All' && u.dataProvider !== providerFilter)
        return false;
      if (marketFilter !== 'All' && marketOf(u.coverageLabel) !== marketFilter)
        return false;
      if (!q) return true;
      return (
        benchmarkCodeFor(u).toLowerCase().includes(q) ||
        officialNameFor(u).toLowerCase().includes(q) ||
        (u.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [availableBenchmarks, search, providerFilter, marketFilter]);

  const handleSelect = (id: string) => {
    onSelect(id);
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Trigger — the benchmark-specific "Add Benchmark" affordance (kept, so
          the multi-select add flow reads clearly), styled like the Figma. */}
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-start h-10">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Search className="w-3 h-3 text-muted-foreground" />
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-[15px]">Add Benchmark</span>
              <span className="text-muted-foreground text-[14px]">
                Select an Index for comparison
              </span>
            </div>
          </div>
          <ChevronDown className="w-3 h-3 ml-auto" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        <div className="flex flex-col max-h-[420px]">
          {/* Search — mirrors the universe picker's search row. */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-b">
            <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search benchmarks..."
              className="flex-1 text-sm outline-none bg-transparent placeholder:text-muted-foreground"
            />
          </div>

          {/* Sticky, transparent filter bar — the SAME control as the universe
              index picker (FilterCombobox provider + market). */}
          <div className="sticky top-0 z-10 flex items-center gap-4 px-3 py-2 border-b border-border/50 bg-background/70 backdrop-blur-lg">
            <FilterCombobox
              allLabel="All providers"
              value={providerFilter}
              options={providerOptions}
              onChange={setProviderFilter}
            />
            <FilterCombobox
              allLabel="All markets"
              value={marketFilter}
              options={marketOptions}
              onChange={setMarketFilter}
            />
          </div>

          {/* List — same IndexPickerRow as the universe picker, wrapped in a
              button (this popover isn't a cmdk Command). */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No benchmarks found.
              </p>
            ) : (
              filtered.map((u) => {
                const stats = universeStats.get(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => handleSelect(u.id)}
                    className="w-full py-3 px-3 text-left cursor-pointer border-b border-border/50 last:border-0 hover:bg-accent transition-colors"
                  >
                    <IndexPickerRow
                      name={officialNameFor(u)}
                      coverageLabel={u.coverageLabel || formatScopeLabel(u.scope)}
                      countries={stats?.countries ?? STATIC_COUNTRY_COUNTS[u.id]}
                      constituents={stats?.constituents}
                      provider={u.dataProvider}
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
